import { getUserTaxonomy } from '../../shared/helpers/trackPage';
import {
    AnalyticsTaxonomy,
    SearchCategory,
    SearchSource
} from 'search/SearchContext';
import { Filters } from '../../reducers/filters';
import { EventNames } from 'shared/tracking/contracts';

const getBaselayer = () => ({
    session_id: window.__global_base_session_id,
    instance_id: window.__global_base_instance_id,
    device_id: window.__global_base_device_id,
    session_count: window.__global_base_session_count
});

const getTaxonomy = (
    analyticsTaxonomy: AnalyticsTaxonomy,
    currentSearchSource: SearchSource,
    searchCategory: SearchCategory
) => ({
    section: analyticsTaxonomy.section,
    search_source: currentSearchSource,
    search_category: searchCategory,
    location: analyticsTaxonomy.areaName,
    search_location: analyticsTaxonomy.searchLocation,
    search_guid: analyticsTaxonomy.searchGuid,
    search_identifier: analyticsTaxonomy.searchIdentifier,
    alias: '',
    full_area_name: analyticsTaxonomy.areaName,
    full_street_name: '',
    country_code: analyticsTaxonomy.countryCode,
    county_area_id: '',
    county_area_name: analyticsTaxonomy.countyAreaName,
    incode: '',
    outcode: analyticsTaxonomy.outcode,
    outcodes: analyticsTaxonomy.outcodes,
    post_town_name: '',
    postal_area: analyticsTaxonomy.postalArea,
    region_name: analyticsTaxonomy.regionName
});

const getFormattedFilters = (
    filters: Filters,
    analyticsTaxonomy: AnalyticsTaxonomy
) => [
    {
        type: 'property_type',
        value: filters.propertyType || analyticsTaxonomy.propertyType
    },
    {
        type: 'property_sub_type',
        value: filters.propertySubType
    },
    { type: 'property_features', value: filters.features },
    { type: 'property_status', value: '' },
    { type: 'pre_owned_or_new_homes', value: filters.newHomes },
    {
        type: 'ownership_type',
        value: filters.isSharedOwnership
    },
    { type: 'retirement', value: filters.retirementHome },
    { type: 'keywords', value: filters.keywords },
    {
        type: 'price_max',
        value: filters.price?.price_max || analyticsTaxonomy.priceMax
    },
    {
        type: 'price_min',
        value: filters.price?.price_min || analyticsTaxonomy.priceMin
    },
    {
        type: 'beds_min',
        value: filters.beds?.beds_min || analyticsTaxonomy.bedsMin
    },
    {
        type: 'beds_max',
        value: filters.beds?.beds_max || analyticsTaxonomy.bedsMax
    },
    { type: 'letting_type', value: '' },
    { type: 'furnishing', value: filters.furnishings },
    { type: 'price_frequency', value: filters.priceFrequency },
    {
        type: 'distance_from_location',
        value: filters.radius || analyticsTaxonomy.radius
    },
    {
        type: 'distance_from_location_auto_expansion',
        value: analyticsTaxonomy.radiusAutoexpansion
    },
    { type: 'include_sold', value: filters.underOfferOrSold }
];

const getSorts = (analyticsTaxonomy: AnalyticsTaxonomy) => [
    { type: 'listings_per_page', value: '' },
    {
        type: 'listings_sort',
        value: analyticsTaxonomy.resultsSort
    },
    {
        type: 'results_view_type',
        value: analyticsTaxonomy.viewType
    }
];

type ProductImpression = {
    // branch_id: string,
    // development_id: string,
    listing_id: string;
    // listing_revision_id: string,
    // branch_name: string, IN GQL
    // developer_name: string,
    // promotion_type: string,
    // position: string, FROM API TAXONOMY
    price: string;
    currency_code: string;
    // listing_status: string,
    page_number: number;
};

const getProduct = (
    listingId: string,
    currencyCode: AnalyticsTaxonomy['currencyCode'],
    pageNumber: number,
    price: string
): ProductImpression => ({
    listing_id: listingId,
    price,
    currency_code: currencyCode,
    page_number: pageNumber
});

type BaseLayer = ReturnType<typeof getBaselayer>;
type Taxonomy = ReturnType<typeof getTaxonomy>;
type FormattedFilters = ReturnType<typeof getFormattedFilters>;
type Sorts = ReturnType<typeof getSorts>;

type Ecommerce = {
    filters: FormattedFilters;
    sorts: Sorts;
    list_name: string;
    products?: [ProductImpression];
    section?: string;
};

type SegmentEventData = {
    user_uuid?: string;
    baselayer: BaseLayer;
    taxonomy: Taxonomy;
    ecommerce: Ecommerce;
};

const sendSegmentEvent = async (
    eventName: string,
    eventData: SegmentEventData
): Promise<void> => {
    const userTaxonomy = await getUserTaxonomy();
    const userUuid = userTaxonomy?.user_uuid;
    const isUserLoggedIn = userTaxonomy?.signed_in_status === 'signed in';

    window.analytics?.track(eventName, {
        ...(isUserLoggedIn && { user_uuid: userUuid }),
        ...eventData
    });
};

export const segmentSearchEvent = (
    analyticsTaxonomy: AnalyticsTaxonomy,
    currentSearchSource: SearchSource,
    searchCategory: SearchCategory,
    filters: Filters
): void => {
    sendSegmentEvent(EventNames.SEARCH, {
        baselayer: getBaselayer(),
        taxonomy: getTaxonomy(
            analyticsTaxonomy,
            currentSearchSource,
            searchCategory
        ),
        ecommerce: {
            filters: getFormattedFilters(filters, analyticsTaxonomy),
            sorts: getSorts(analyticsTaxonomy),
            list_name: analyticsTaxonomy.listingsCategory
        }
    });
};

const mapResultClickAndImpression = (
    analyticsTaxonomy: AnalyticsTaxonomy,
    currentSearchSource: SearchSource,
    searchCategory: SearchCategory,
    filters: Filters,
    listingId: string,
    price: string,
    pageNumber: number
): SegmentEventData => ({
    baselayer: getBaselayer(),
    taxonomy: getTaxonomy(
        analyticsTaxonomy,
        currentSearchSource,
        searchCategory
    ),
    ecommerce: {
        filters: getFormattedFilters(filters, analyticsTaxonomy),
        sorts: getSorts(analyticsTaxonomy),
        list_name: analyticsTaxonomy.listingsCategory,
        products: [
            getProduct(
                listingId,
                analyticsTaxonomy.currencyCode,
                pageNumber,
                price
            )
        ],
        section: analyticsTaxonomy.section
    }
});

export const segmentSearchResultClickEvent = (
    analyticsTaxonomy: AnalyticsTaxonomy,
    currentSearchSource: SearchSource,
    searchCategory: SearchCategory,
    filters: Filters,
    listingId: string,
    price: string,
    pageNumber: number
): void => {
    const eventData = mapResultClickAndImpression(
        analyticsTaxonomy,
        currentSearchSource,
        searchCategory,
        filters,
        listingId,
        price,
        pageNumber
    );

    sendSegmentEvent(EventNames.CLICK_SEARCH_RESULT, eventData);
};

export const segmentSearchResultImpressionEvent = (
    analyticsTaxonomy: AnalyticsTaxonomy,
    currentSearchSource: SearchSource,
    searchCategory: SearchCategory,
    filters: Filters,
    listingId: string,
    price: string,
    pageNumber: number
): void => {
    const eventData = mapResultClickAndImpression(
        analyticsTaxonomy,
        currentSearchSource,
        searchCategory,
        filters,
        listingId,
        price,
        pageNumber
    );

    sendSegmentEvent(EventNames.SEARCH_RESULT_IMPRESSION, eventData);
};
